import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { getMe } from "../services/auth.api";
import { isAccountType, type AuthUser } from "../types";
import { getStoredAuthToken, subscribeToAuthTokenChange, subscribeToUserChange } from "../utils/authToken";

export function useUser() {
  const [authToken, setAuthToken] = useState<string | null>(null);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [status, setStatus] = useState<"loading" | "authenticated" | "unauthenticated">("loading");

  const [isInitializing, setIsInitializing] = useState(true);

  useEffect(() => {
    const token = getStoredAuthToken();
    if (token) {
      setAuthToken(token);
      setIsInitializing(false);
    } else {
      import("@/services/apiClient").then(({ initializeAuthFromSession }) => {
        initializeAuthFromSession().then((newToken) => {
          if (newToken) {
            setAuthToken(newToken);
          } else {
            setStatus("unauthenticated");
          }
          setIsInitializing(false);
        });
      });
    }
  }, []);

  useEffect(() => subscribeToAuthTokenChange(() => setAuthToken(getStoredAuthToken())), []);

  // Monotonic guard for /api/me. Every request captures a sequence number and
  // only the most recent one is allowed to write identity state. Several
  // refetches can be in flight at once for a single profile switch (the token
  // `storage` event AND an explicit cross-tab refetch), and a slow response
  // issued under a *previous* token could otherwise land last and restore the
  // wrong profile. Superseded responses are dropped.
  const requestSeqRef = useRef(0);

  const markUnauthenticated = useCallback(() => {
    // Bump the sequence so any in-flight /api/me can't resurrect a user after
    // we've decided the session is gone (e.g. token cleared).
    requestSeqRef.current += 1;
    setUser(null);
    setStatus("unauthenticated");
  }, []);

  const refetchUser = useCallback(async () => {
    const seq = (requestSeqRef.current += 1);
    setStatus("loading");
    try {
      const data = await getMe();
      if (seq !== requestSeqRef.current) {
        return; // a newer request superseded this one — drop the stale result
      }
      // A valid dashboard session must carry a student/teacher account_type.
      // Admin accounts (or any unexpected payload) are treated as
      // unauthenticated for this app.
      if (isRecord(data) && isAccountType(data.account_type)) {
        setUser(data as AuthUser);
        setStatus("authenticated");
      } else {
        setUser(null);
        setStatus("unauthenticated");
      }
    } catch {
      if (seq !== requestSeqRef.current) {
        return;
      }
      setUser(null);
      setStatus("unauthenticated");
    }
  }, []);

  useEffect(() => {
    if (isInitializing) {
      return;
    }

    if (!authToken) {
      markUnauthenticated();
      return;
    }

    void refetchUser();
  }, [authToken, isInitializing, refetchUser, markUnauthenticated]);

  // Refresh user data when other parts of the app notify a user update (e.g.,
  // profile changed, or a cross-tab profile switch propagated via the token
  // `storage` event).
  useEffect(
    () => subscribeToUserChange(() => void refetchUser()),
    [refetchUser]
  );

  // Memoize the returned object so consumers (e.g. CurrentUserProvider ->
  // DashboardLayoutClient) only re-render when the identity/status actually
  // changes. Without this, the fresh object literal on every render churned
  // every consumer on each intermediate `loading` transition, recomputing the
  // identity `key` and remounting/refetching the dashboard more than once per
  // switch. `refetchUser` is stable (useCallback []).
  return useMemo(
    () => ({
      user,
      loading: status === "loading",
      isAuthenticated: status === "authenticated",
      isUnauthenticated: status === "unauthenticated",
      refetchUser,
    }),
    [user, status, refetchUser]
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

